// eslint:disable:max-func-body-length
import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { STATE } from 'app/constants';
import { actionCreators } from './actions';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { ReportId, savedReportTranslation } from 'app/area/saved-reports/constants';
import { FullscreenCodeSelector } from 'app/components';
import { routeConstants } from 'app/routes/route-constants';
import { toUpdateSavedReport } from 'app/area/shared/navigation';
import { SelectOptionValue, SelectOptions } from 'app/area/code-selector/types';
import { PromptConfirmation } from 'ui';
import { generateCopyLinkSearch, generateCopyLinkCompanyReport, generateCopyLinkDashboard }
  from 'app/area/saved-reports/generate-copy-link';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import {
  simpleFieldForPath, FIELD_TYPE, withSimpleForm, SimpleFormValidation, SimpleFormValidationConfig, requiredValidation
} from 'app/infrastructure/simple-form';
import { form, endpoint } from './constants';
import { SerializeType } from 'app/infrastructure/simple-form/types';
import { CreateFolder } from 'app/area/saved-reports/create-folder/create-folder';
import { copyToClipboard, replaceTemplateString, insertOptionalWordbreaks } from 'app/infrastructure/platform';
import { NewFolderData } from 'app/area/saved-reports/types';
import { MdGroup, MdInsertDriveFile } from 'react-icons/md';
import get from 'lodash/get';

import styles from './create-report.scss';
import { Tooltip } from 'ui/common/tooltip/tooltip';
import { EditFolder } from 'app/area/saved-reports/edit-folder/edit-folder';
import { FavouriteDefinition } from 'app/types-shared';
import { MdStar, MdOutlineStarOutline } from 'react-icons/md';
import { ViewMode } from 'app/area/saved-reports/types-store';
import { FolderType } from 'app/area/case-report/case-listing/types';
import { ModalSize } from 'app/components/constants';

interface SelectSavedReportProps extends DefaultContainerProps {
  loadSavedReports: (reportCategories: number[]) => Promise<void>;
  removeReport: (id: number, title: string, unshare?: boolean) => Promise<void>;
  moveReport: (id: number, parentFolderId: number, reportCategories: number[], isFolder: boolean) => Promise<void>;
  loadFolderData: (id: number) => Promise<void>;
  clearFolderData: () => Promise<void>;
  folderDefinition: FavouriteDefinition;
  fetchingFolder: boolean;
  reports: SelectOptions;
  copyLinkNotification: (isSuccess: boolean) => Promise<void>;
  formValues: any;
  fetchingCreateFolder: boolean;
  validate: SimpleFormValidation;
  saveFolder: (data: NewFolderData) => Promise<void>;
  searchRequiredUsers?: boolean;
  reportCategories?: number[];
  folderType?: number;
  selectedReport?: number;
  bookmarkedReports?: SelectOptions;
  viewMode?: ViewMode;
  viewModeCompanyReport?: ViewMode;
  viewModeDashboard?: ViewMode;
  changeViewMode?: (mode: ViewMode) => Promise<void>;
  changeViewModeCompanyReport?: (mode: ViewMode) => Promise<void>;
  changeViewModeDashboard?: (mode: ViewMode) => Promise<void>;
  loadBookmarkedSavedReports: (reportCategories: number[]) => Promise<void>;
}

interface SelectSavedReportState {
  itemToDelete?: SelectOptionValue;
  itemToMove?: SelectOptionValue;
  isToggledOn?: boolean;
  createFolder?: boolean;
  createSubfolder?: boolean;
  parentFolder?: number;
  itemToEdit?: SelectOptionValue;
}

const SimpleForm = simpleFieldForPath(STATE.savedReport);

const createMenuItem = (title: string, onClick, key: number) => <li key={key}>
  <button type="button" className="dropdown-item" onClick={onClick}>
    {title}
  </button>
</li>;

const isCompanyReport = (selectedReport: number): boolean => {
  return !!(selectedReport === ReportId.CompanyReport);
};

const isDashboard = (selectedReport: number): boolean => {
  return !!(selectedReport === ReportId.Dashboard);
};

class SelectSavedReportSelector extends React.Component<SelectSavedReportProps & typeof actionCreators, SelectSavedReportState> {
  public static statePath: string = STATE.savedReport;

  public state: Readonly<SelectSavedReportState> = {
    itemToDelete: null,
    itemToMove: null,
    createFolder: false,
    createSubfolder: false,
    parentFolder: null,
    itemToEdit: null,
    isToggledOn: isCompanyReport(this.props.selectedReport) ? this.props.viewModeCompanyReport === ViewMode.bookmarkedReports :
      isDashboard(this.props.selectedReport) ? this.props.viewModeDashboard === ViewMode.bookmarkedReports :
        this.props.viewMode === ViewMode.bookmarkedReports
  };

  public componentDidMount() {
    this.props.getSearchRequiredUsers && this.props.getSearchRequiredUsers();
    this.props.loadSavedReports && this.loadSavedReports();
    this.props.loadBookmarkedSavedReports && this.loadBookmarkedSavedReports();
  }

  private handleRequestDelete = (item: SelectOptionValue) => {
    this.setState({
      itemToDelete: item
    });
  };

  private handleCancelDelete = () => {
    this.setState({
      itemToDelete: null
    });
  };

  private handleConfirmDelete = () => {
    const shared = this.state.itemToDelete.owner.value !== this.props.sharedState.id;
    const { removeReport, reportCategories } = this.props;
    const { itemToDelete } = this.state;

    removeReport(
      itemToDelete.value,
      itemToDelete.description, shared, reportCategories,
      !!itemToDelete.descendants
    );

    this.setState({
      itemToDelete: null
    });
  };

  private renderItemLabel = (item: SelectOptionValue) => {
    const { sharedState, translation } = this.props;
    const { owner, sharedThroughUserGroup } = item;
    const owned = item.owner.value === sharedState.id;
    const isFolder = item.descendants;

    if (owned) {
      return null;
    }

    const messageSharedReportGroup = isDashboard(this.props.selectedReport) ? translation.shared_dashboard_group :
      translation.shared_report_group;
    const messageSharedReport = isDashboard(this.props.selectedReport) ? translation.shared_dashboard :
      translation.shared_report;
    const messageSharedFolderGroup = isDashboard(this.props.selectedReport) ? translation.shared_dashboard_folder_group :
      translation.shared_folder_group;

    const message = sharedThroughUserGroup ?
      (isFolder ? messageSharedFolderGroup : messageSharedReportGroup) :
      (isFolder ? translation.shared_folder_user : messageSharedReport);

    const label = <div className={styles.label}>{translation.shared} {sharedThroughUserGroup && <MdGroup />}</div>;

    if (owner.value === 0) {
      return label;
    }

    return <Tooltip center={true} title={replaceTemplateString(message, owner.description)}>
      {label}
    </Tooltip>;
  };

  private selectedItem = () => {
    const { router } = this.props;
    return router?.location?.query?.favouriteId && Number(router?.location?.query?.favouriteId);
  };

  private renderItemBookmark = (item: SelectOptionValue) => {
    const { translation, selectedReport } = this.props;
    const isFolder = item.descendants;
    const titleBookmarkReport = isDashboard(selectedReport) ? translation.bookmark_dashboard : translation.bookmark_report;

    const toggleBookmark = async (e: React.MouseEvent<any>) => {
      const { bookmarkReport, removeBookmark, reportCategories } = this.props;

      e?.preventDefault();
      e?.stopPropagation();
      const selectedItem = this.selectedItem();
      if (item.bookmark) {
        removeBookmark(item.value, selectedItem, reportCategories);
      }
      else {
        bookmarkReport(item.value, selectedItem, reportCategories);
      }
    };

    return (
      !isFolder && (
        item.bookmark ?
          <div className={styles.bookMarked} onClick={toggleBookmark}>
            <Tooltip title={translation.remove_bookmark} delayed={true} center={true}>
              <MdStar />
            </Tooltip>
          </div> :
          <div className={styles.unbookMarked} onClick={toggleBookmark}>
            <Tooltip title={titleBookmarkReport} delayed={true} center={true}>
              <MdOutlineStarOutline />
            </Tooltip>
          </div>
      ));
  };

  private renderItemDropdown = (item: SelectOptionValue) => {
    const { translation, copyLinkNotification, sharedState, selectedReport, router } = this.props;

    const owned = item.owner.value === sharedState.id;

    const deleteClick = () => {
      this.handleRequestDelete(item);
    };

    const copyLink = () => {
      const link = isCompanyReport(selectedReport) ? generateCopyLinkCompanyReport(item.value) :
        isDashboard(selectedReport) ? generateCopyLinkDashboard(item.value) : generateCopyLinkSearch(item.value);
      const copyStatus = copyToClipboard(link);
      copyLinkNotification(copyStatus);
    };

    const moveReport = () => {
      this.setState({ itemToMove: item });
    };

    const editReport = () => {

      if (isCompanyReport(this.props.selectedReport)) {
        this.props.toUpdateCompanyReport(item.value);
      }
      else if (isDashboard(this.props.selectedReport)) {
        this.props.toUpdateDashboard(item.value);
      }
      else {
        toUpdateSavedReport(item.value);
      }
    };

    const editFolder = () => {
      this.setState({
        itemToEdit: item
      });
      this.props.loadFolderData(item.value);
    };

    const toggleBookmark = async () => {
      const { bookmarkReport, removeBookmark, reportCategories } = this.props;
      const selectedItem = this.selectedItem();
      if (item.bookmark) {
        removeBookmark(item.value, selectedItem, reportCategories);
      }
      else {
        bookmarkReport(item.value, selectedItem, reportCategories);
      }
    };

    const createFolder = () => {
      const selectedFolder = router?.location?.query?.folder && Number(router?.location?.query?.folder);

      this.setState({
        parentFolder: selectedFolder,
        createFolder: true
      });
    };

    const createSubFolder = () => {
      this.setState({
        parentFolder: item.value,
        createSubfolder: true
      });
    };

    const parentFolderNotShared = (): boolean => {
      const { reports } = this.props;
      const isNotRootFolder = !!Number(router?.location?.query?.folder);
      const currentFolder = isNotRootFolder && Number(router?.location?.query?.folder);
      return !currentFolder ? true : reports.items.some(report => report.value === currentFolder &&
        report.owner.value === sharedState.id);
    };

    const openAsAdministrator = () => {
      navigate.toAdminFavourite(item?.value);
    };

    const titleRemoveReport = isDashboard(selectedReport) ? translation.remove_dashboard : translation.remove_report;
    const titleMoveReport = isDashboard(selectedReport) ? translation.move_dashboard : translation.move_report;
    const titleEditReport = isDashboard(selectedReport) ? translation.edit_dashboard : translation.edit_report;
    const titleBookmarkReport = isDashboard(selectedReport) ? translation.bookmark_dashboard : translation.bookmark_report;
    const hasAdminPanelAccess = !!sharedState?.access?.adminPanel?.favourite?.administrationCentral;

    const menuItems = [
      (parentFolderNotShared()) &&
      createMenuItem(translation.new_folder, createFolder, 1),
      owned && item.descendants &&
      createMenuItem(translation.new_subfolder, createSubFolder, 2),
      owned && !item.descendants &&
      createMenuItem(titleEditReport, editReport, 3),
      owned && item.descendants &&
      createMenuItem(translation.edit_folder, editFolder, 3),
      owned &&
      createMenuItem(item.descendants ? translation.move_folder : titleMoveReport, moveReport, 4),
      (owned || !item.sharedThroughUserGroup) &&
      createMenuItem(item.descendants ? translation.remove_folder : titleRemoveReport, deleteClick, 5),
      !item.descendants &&
      createMenuItem(translation.copy_to_clipboard, copyLink, 6),
      (!item.descendants && (!!item.bookmark ? createMenuItem(translation.remove_bookmark, toggleBookmark, 7) :
        createMenuItem(titleBookmarkReport, toggleBookmark, 7))
      ),
      hasAdminPanelAccess &&
      createMenuItem(translation.open_as_administrator, openAsAdministrator, 8)
    ].filter(i => i);

    return menuItems.length !== 0 ? menuItems : null;
  };

  private moveFolder = () => {

    const selectedFolderId = get(this, `props.formValues.${form.folder}.value`);
    const id = get(this, 'state.itemToMove.value');
    const { moveReport, reportCategories } = this.props;
    const isFolder = !!get(this, 'state.itemToMove.descendants');
    moveReport(id, selectedFolderId, reportCategories, isFolder);
  };

  private closeMoveFolder = () => {
    this.setState({
      itemToMove: null
    });
  };

  private closeCreateFolder = () => {
    this.setState({
      createFolder: false,
      parentFolder: null
    });
  };

  private closeCreateSubfolder = () => {
    this.setState({
      createSubfolder: false,
      parentFolder: null
    });
  };

  private closeEditFolder = () => {
    this.props.clearFolderData();
    this.setState({
      itemToEdit: null
    });
  };

  private loadSavedReports = () => {
    this.props.loadSavedReports(this.props.reportCategories);
  };

  private loadBookmarkedSavedReports = () => {
    this.props.loadBookmarkedSavedReports(this.props.reportCategories);
  };

  private routingToSpecificFolder = (): boolean => {
    const { router } = this.props;
    return !!router?.location?.query?.folder && !!Number(!!router?.location?.query?.folder);
  };

  private routingToSpecificFavourite = (): boolean => {
    const { router } = this.props;
    return !!router?.location?.query?.favouriteId && !!Number(!!router?.location?.query?.favouriteId);
  };

  private handleOnToggle = async (isToggledOn: boolean) => {

    const { selectedReport, changeViewMode, changeViewModeCompanyReport, changeViewModeDashboard } = this.props;
    const changeViewModeOnToggle = isCompanyReport(selectedReport) ? changeViewModeCompanyReport :
      isDashboard(selectedReport) ? changeViewModeDashboard : changeViewMode;

    isToggledOn && navigate.toSelectSavedReports();
    if (!this.routingToSpecificFavourite()) {
      isToggledOn ? await changeViewModeOnToggle(ViewMode.bookmarkedReports) : await changeViewModeOnToggle(ViewMode.allReports);
    }

    this.setState({
      isToggledOn: isToggledOn
    });
  };

  private onClose = () => {
    navigate.toStartupView();
  };

  private onOpen = () => {

    if (this.routingToSpecificFavourite() || this.routingToSpecificFolder()) {
      this.handleOnToggle(false);
      return;
    }

    if (!this.state.isToggledOn) {
      this.handleOnToggle(false);
      return;
    }
  };

  private validationConfig: SimpleFormValidationConfig = [
    requiredValidation(form.create_folder_input)
  ];

  private onCreateFolder = () => {
    const { validate, formValues, selectedReport, reportCategories, saveFolder } = this.props;
    const { parentFolder } = this.state;

    validate(this.validationConfig, () => {
      saveFolder({
        name: formValues[form.create_folder_input],
        ...(parentFolder && { parentFolder }),
        ...(selectedReport === ReportId.CompanyReport && {
          folderType: FolderType.SpecialReport
        } ||
          selectedReport === ReportId.Dashboard && {
            folderType: FolderType.Dashboard
          })
      }, reportCategories);
    });
  };

  public render() {
    const { translation, reports, folderDefinition, fetchingFolder, searchRequiredUsers, fetchingCreateFolder,
      reportCategories, folderType, selectedReport, bookmarkedReports, router, formValues } = this.props;
    const { itemToDelete, itemToMove, isToggledOn, createFolder, createSubfolder, itemToEdit } = this.state;
    const modalTitle = isCompanyReport(selectedReport) ? translation.all_saved_company_reports_title :
      isDashboard(selectedReport) ? translation.all_saved_dashboards_title : translation.all_saved_reports_title;
    const modalTitleBookmarks = isCompanyReport(selectedReport) ? translation.bookmarked_company_reports_title :
      isDashboard(selectedReport) ? translation.bookmarked_dashboards_title : translation.bookmarked_reports_title;
    const urlResolver = isCompanyReport(selectedReport) ? routeConstants.savedCompanyReportFor :
      isDashboard(selectedReport) ? routeConstants.savedDashboardFor : routeConstants.savedReportFor;
    const selectedFolder = router?.location?.query?.folder && Number(router?.location?.query?.folder);
    const selectedItem = this.selectedItem();
    const textNoReportsAdded = isCompanyReport(selectedReport) ? translation.no_saved_company_reports :
      isDashboard(selectedReport) ? translation.no_saved_dashboards : translation.no_saved_reports;
    const textNoBookmarksAdded = isCompanyReport(selectedReport) ? translation.no_bookmarked_company_reports :
      isDashboard(selectedReport) ? translation.no_bookmarked_dashboards : translation.no_bookmarked_reports;
    const options = isToggledOn ? bookmarkedReports.items : reports.items;
    const title = isToggledOn ? modalTitleBookmarks : modalTitle;
    const textToggleTooltip = isDashboard(selectedReport) ? translation.toggle_between_dashboards_and_bookmarks :
      translation.toggle_between_reports_and_bookmarks;
    const fetching = isToggledOn ? bookmarkedReports?.fetching : reports?.fetching;
    const textMoveReport = isDashboard(selectedReport) ? translation.move_dashboard : translation.move_report;
    const textRemoveReport = isDashboard(selectedReport) ? translation.remove_dashboard : translation.remove_report;
    const textConfirmRemoveFolder = isDashboard(selectedReport) ? translation.confirm_remove_dashboard_folder :
      translation.confirm_remove_folder;

    return <><FullscreenCodeSelector
      onOpen={this.onOpen}
      onClose={this.onClose}
      fetching={fetching}
      options={options}
      title={title}
      urlResolver={urlResolver}
      textGoBack={translation.back}
      routeHandler={router.replace}
      itemDropdownRender={this.renderItemDropdown}
      itemLabelRender={this.renderItemLabel}
      defaultIcon={<MdInsertDriveFile className={`${styles.selectCodeBtnIcon} ${styles.defaultIcon}`} />}
      showAllOptionsWhenSingleFolderExists={false}
      selectedFolder={selectedFolder}
      selectedItem={selectedItem}
      showToggleButton={true}
      onToggle={this.handleOnToggle}
      isToggledOn={isToggledOn}
      subtitleToggledOff={textNoReportsAdded}
      subtitleToggledOn={textNoBookmarksAdded}
      itemBookmark={this.renderItemBookmark}
      textSwitchTooltip={textToggleTooltip}
    />
      {itemToDelete && <PromptConfirmation
        title={itemToDelete.descendants ? translation.remove_folder : textRemoveReport}
        cancelText={translation.cancel}
        continueText={translation.remove}
        description={
          insertOptionalWordbreaks(replaceTemplateString(
            itemToDelete.descendants ? textConfirmRemoveFolder : translation.confirm_remove,
            `"${itemToDelete.description}"`))
        }
        onCancel={this.handleCancelDelete}
        onContinue={this.handleConfirmDelete} />
      }
      {(fetchingFolder || folderDefinition) &&
        <EditFolder
          onClose={this.closeEditFolder}
          definition={folderDefinition}
          searchRequiredUsers={searchRequiredUsers}
          reportCategories={reportCategories}
          folderType={folderType}
          hasDescendants={!!itemToEdit?.descendants?.length}
          ownerParent={reports?.items?.filter(i => i.descendants?.some(d => d?.value === itemToEdit?.value))[0]?.owner?.value}
          {...this.props}
        />}

      {itemToMove && <ModalWrapper
        onClose={this.closeMoveFolder}
        onDone={this.moveFolder}
        closeOnDone={true}
        btnTitle={translation.move}
        headerTitle={itemToMove.descendants ? translation.move_folder : textMoveReport}
        size={ModalSize.large}
      >
        <SimpleForm
          className={styles.createFolder}
          name={form.folder}
          type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
          optionsEndpoint={endpoint.loadFolderOptions}
          optionsParams={{ folderType, sharedWithCurrentUser: false }}
          label={{ text: translation.folder }}
          initialValue={''}
          translation={translation}
          placeholder={translation.search_placeholder}
          serialize={{ type: SerializeType.V_VALUE }} />
        <CreateFolder
          className={styles.createFolder}
          {...this.props}
          translation={translation}
          fetching={fetchingCreateFolder}
          selectedReport={selectedReport} />
      </ModalWrapper>}

      {createFolder && <ModalWrapper
        onClose={this.closeCreateFolder}
        onDone={this.onCreateFolder}
        closeOnDone={!!(formValues[form.create_folder_input])?.length}
        btnTitle={translation.create_folder_button}
        headerTitle={translation.create_new_folder}
        size={ModalSize.large}
      >
        <CreateFolder
          className={styles.createFolder}
          {...this.props}
          translation={translation}
          fetching={fetchingCreateFolder}
          selectedReport={selectedReport}
          reportCategories={reportCategories}
          hideLinkButtonCreateFolder={true}
          hideLabel={true}
          hideCreateButton={true}
        />
      </ModalWrapper>}

      {createSubfolder && <ModalWrapper
        onClose={this.closeCreateSubfolder}
        onDone={this.onCreateFolder}
        closeOnDone={!!(formValues[form.create_folder_input])?.length}
        btnTitle={translation.create_folder_button}
        headerTitle={translation.create_new_subfolder}
        size={ModalSize.large}
      >
        <CreateFolder
          className={styles.createFolder}
          {...this.props}
          translation={translation}
          fetching={fetchingCreateFolder}
          selectedReport={selectedReport}
          reportCategories={reportCategories}
          hideLinkButtonCreateFolder={true}
          hideLabel={true}
          hideCreateButton={true}
        />
      </ModalWrapper>}
    </>;
  }
}

const container = containerFactory(withSimpleForm(SelectSavedReportSelector), actionCreators, undefined, savedReportTranslation);

export { container as SelectSavedReportSelector };
